import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Type,
} from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ModalService {
  private appRef = inject(ApplicationRef);

  private modalRef: HTMLDialogElement | null = null;
  private componentRef: ComponentRef<any> | null = null;

  private result$ = new Subject<any>();

  open<T extends object, R = any>(
    component: Type<T>,
    data?: Partial<T>,
    envInjector?: EnvironmentInjector
  ) {
    const dialog = document.createElement('dialog');
    dialog.className = 'modal modal-open';

    const modalBox = document.createElement('div');
    modalBox.className = 'modal-box';
    dialog.appendChild(modalBox);
    document.body.appendChild(dialog);

    const compRef = createComponent(component, {
      environmentInjector: envInjector ?? this.appRef.injector,
    });

    if (data) Object.assign(compRef.instance, data);

    // 🔑 inject closeWithResult into modal component
    (compRef.instance as any).closeWithResult = (result: R) => {
      this.result$.next(result);
      this.result$.complete();
      this.cleanup();
      this.result$ = new Subject<R>();
    };

    modalBox.appendChild(compRef.location.nativeElement);
    this.appRef.attachView(compRef.hostView);

    dialog.showModal();

    this.modalRef = dialog;
    this.componentRef = compRef;

    return this.result$.asObservable();
  }

  private cleanup() {
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }

    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef.remove();
      this.modalRef = null;
    }
  }

  close() {
    this.cleanup();
  }
}
